'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { formatters } from '@morpho-org/ui-v6/utils';
import { ArrowDiagonalDown, ArrowDiagonalUp } from '@repo/ui/icons';
import { cva, type VariantProps } from 'class-variance-authority';
import { type ForwardedRef, forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { Skeleton } from '../server/Skeleton';
export type VariationValuesType = {
  present: number;
  past: number;
} | null;
export type VariationDisplayMode = 'both' | 'percentage' | 'amount';

/**
 * Icon container variants using cva for Tailwind class management.
 *
 * Matches v5 VariationLabel icon container behavior:
 * - Background: valid (green) for positive, alert (red) for negative
 * - Border radius: full (circle)
 */
const iconContainerVariants = cva(
// Base styles
'flex items-center justify-center rounded-full', {
  variants: {
    isPositive: {
      true: 'bg-success-foreground/24',
      false: 'bg-destructive-foreground/18'
    }
  },
  defaultVariants: {
    isPositive: true
  }
});
const variationIconVariants = cva('', {
  variants: {
    isPositive: {
      true: 'text-success-foreground',
      false: 'text-destructive-foreground'
    }
  },
  defaultVariants: {
    isPositive: true
  }
});
export interface VariationLabelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'>, VariantProps<typeof iconContainerVariants> {
  /** The variation values containing present and past numbers */
  values: VariationValuesType;
  /** Whether the component is in loading state */
  isLoading?: boolean;
  /** Unit for the amount display (default: "$") */
  unit?: string;
  /** Number of digits for amount formatting (default: 2) */
  digits?: number;
  /** Display mode: "both" shows amount (percentage), "percentage" shows only percentage, "amount" shows only amount */
  displayMode?: VariationDisplayMode;
  /** Additional Tailwind classes for the text span (v5 textVariant parity) */
  textClassName?: string;
}
interface VariationData {
  isPositive: boolean;
  amount: string;
  percentage: string | null;
}
function computeVariation({
  values,
  unit,
  digits
}: {
  values: VariationValuesType;
  unit: string | undefined;
  digits: number;
}): VariationData | undefined {
  if (values === null) return;
  const amount = values.present - values.past;
  const percentage = values.past === 0 ? null : amount / values.past;
  return {
    isPositive: amount >= 0,
    amount: formatters.short.sign().unit(unit).digits(digits).of(amount),
    percentage: percentage === null ? null : formatters.percent.sign().trailingZero(false).unit('%').digits(2).of(percentage)
  };
}
function getDisplayText(variation: VariationData | undefined, displayMode: VariationDisplayMode): ReactNode {
  if (!variation) return null;
  switch (displayMode) {
    case 'percentage':
      return variation.percentage ?? variation.amount;
    case 'amount':
      return variation.amount;
    case 'both':
    default:
      return <>
          {variation.amount} {variation.percentage !== null && `(${variation.percentage})`}
        </>;
  }
}

/**
 * VariationLabel component - Tailwind replica of dapp-ui-v5 VariationLabel
 *
 * Features:
 * - Displays variation between two values with icon and formatted text
 * - Green arrow up for positive changes, red arrow down for negative
 * - Supports different display modes: both, percentage, or amount
 * - Loading skeleton state
 *
 * @example
 * ```tsx
 * // Show both amount and percentage change
 * <VariationLabel
 *   values={{ present: 1100, past: 1000 }}
 *   unit="$"
 * />
 * // Displays: ↗ +$100 (+10%)
 *
 * // Show only percentage
 * <VariationLabel
 *   values={{ present: 1100, past: 1000 }}
 *   displayMode="percentage"
 * />
 * // Displays: ↗ +10%
 *
 * // Loading state
 * <VariationLabel values={null} isLoading />
 * ```
 */
const VariationLabel = (t0, ref) => {
  const $ = _c(48);
  let className;
  let onClick;
  let props;
  let t1;
  let t2;
  let t3;
  let textClassName;
  let unit;
  let values;
  if ($[0] !== t0) {
    ({
      className,
      values,
      isLoading: t1,
      unit,
      digits: t2,
      displayMode: t3,
      textClassName,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onClick;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = textClassName;
    $[8] = unit;
    $[9] = values;
  } else {
    className = $[1];
    onClick = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    textClassName = $[7];
    unit = $[8];
    values = $[9];
  }
  const isLoading = t1 === undefined ? false : t1;
  const digits = t2 === undefined ? 2 : t2;
  const displayMode = t3 === undefined ? "both" : t3;
  let displayText;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let variation;
  if ($[10] !== className || $[11] !== digits || $[12] !== displayMode || $[13] !== isLoading || $[14] !== onClick || $[15] !== props || $[16] !== ref || $[17] !== unit || $[18] !== values) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      variation = computeVariation({
        values,
        unit,
        digits
      });
      displayText = getDisplayText(variation, displayMode);
      if (isLoading) {
        let t10;
        if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <div className="h-md gap-2xs flex flex-row items-center"><Skeleton className="h-md w-5 rounded-full" /><Skeleton className="h-md w-[60px] rounded-[20px]" /></div>;
          $[27] = t10;
        } else {
          t10 = $[27];
        }
        t9 = t10;
        break bb0;
      }
      if (!variation) {
        t9 = null;
        break bb0;
      }
      t5 = ref;
      const t10 = onClick && "cursor-pointer";
      if ($[28] !== className || $[29] !== t10) {
        t6 = cn("h-md gap-2xs flex flex-row items-center", t10, className);
        $[28] = className;
        $[29] = t10;
        $[30] = t6;
      } else {
        t6 = $[30];
      }
      t7 = onClick;
      t8 = props;
      t4 = iconContainerVariants({
        isPositive: variation.isPositive
      });
    }
    $[10] = className;
    $[11] = digits;
    $[12] = displayMode;
    $[13] = isLoading;
    $[14] = onClick;
    $[15] = props;
    $[16] = ref;
    $[17] = unit;
    $[18] = values;
    $[19] = displayText;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = variation;
  } else {
    displayText = $[19];
    t4 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
    variation = $[26];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[31] !== variation.isPositive) {
    t10 = variation.isPositive ? <ArrowDiagonalUp className={variationIconVariants({
      isPositive: true
    })} /> : <ArrowDiagonalDown className={variationIconVariants({
      isPositive: false
    })} />;
    $[31] = variation.isPositive;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t4) {
    t11 = <div className={t4}>{t10}</div>;
    $[33] = t10;
    $[34] = t4;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== textClassName) {
    t12 = cn("text-sm text-foreground leading-none", textClassName);
    $[36] = textClassName;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== displayText || $[39] !== t12) {
    t13 = <span className={t12}>{displayText}</span>;
    $[38] = displayText;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t11 || $[42] !== t13 || $[43] !== t5 || $[44] !== t6 || $[45] !== t7 || $[46] !== t8) {
    t14 = <div ref={t5} className={t6} onClick={t7} {...t8}>{t11}{t13}</div>;
    $[41] = t11;
    $[42] = t13;
    $[43] = t5;
    $[44] = t6;
    $[45] = t7;
    $[46] = t8;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
};
const ForwardedVariationLabel = forwardRef(VariationLabel);
ForwardedVariationLabel.displayName = 'VariationLabel';
export { ForwardedVariationLabel as VariationLabel };